import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { InterpolationMethod, SAMPLE_FROM_SCALARS } from '@/services/calculationRunner.constants';
import { I_CHART_KEY, MR_CHART_KEY, R_CHART_KEY, S_CHART_KEY, XBAR_CHART_KEY } from '@/summaryReport/summaryData.types';

export { InterpolationMethod };

export const STATISTIC_THICKNESS_DISCRETE = 10;
export const STATISTIC_THICKNESS_DEFAULT = 1;

export enum StatisticType {
  MEAN_PVALUE = 'meanpvalue',
  VARIANCE_PVALUE = 'variancepvalue',
  PPK = 'ppk',
  CPK = 'cpk',
  P_VALUE = 'pValue',
}

export const PRIMARY_CHART_SLOT = 'primary' as const;
export const SECONDARY_CHART_SLOT = 'secondary' as const;

export const OVERALL_RUN_RULE = 'Overall' as const;

export const RUN_RULES = {
  WESTERN_ELECTRIC: {
    WE1: 'WE1',
    WE2: 'WE2',
    WE3: 'WE3',
    WE4: 'WE4',
  },
} as const;

export const RUN_RULE_VIOLATION_PATTERN = /WE(\d+)RuleViolationOutput$/;

export const MODEL_NAME = SeeqNames.ModelNames.OperationalizeStatistics;
export const PRIMARY = 'Primary';
export const SECONDARY = 'Secondary';
const ICON_SERIES = 'fc fc-series';
const ICON_SCALAR = 'fc fc-scalar';
const ICON_CAPSULE_SET = 'fc fc-capsule-set';
export const DEFAULT_COLOR = '#000000';

export const ALIGNMENT_CONDITION_WEEKS = 'weeks';

export const TIMESTAMP_START = 'start' as const;
export const TIMESTAMP_END = 'end' as const;
export const TIMESTAMP_DURATION = 'duration' as const;

export const DEFAULT_TIMESTAMP_PLACEMENT = TIMESTAMP_END;
export const DEFAULT_INTERPOLATION_METHOD = InterpolationMethod.DISCRETE;
export const DEFAULT_MAX_CAPSULE_DURATION = { value: 40, units: 'h' } as const;

export const DEFAULT_STATE = {
  selectedStatistics: [],
  timestampPlacement: DEFAULT_TIMESTAMP_PLACEMENT,
  interpolationMethod: DEFAULT_INTERPOLATION_METHOD,
  maxCapsuleDuration: DEFAULT_MAX_CAPSULE_DURATION,
} as const;

export const INPUT_NAMES = {
  signalInput: 'signalInput',
  baselineInput: 'baselineInput',
  restrictOutputToConditionInput: 'conditionInput',
  producePrimaryChart: 'producePrimaryChartInput',
  produceSecondaryChart: 'produceSecondaryChartInput',
  removeLongerThan: 'removeLongerThanInput',
  periodicConditionInput: 'periodicConditionInput',
  alignmentConditionInput: 'alignmentConditionInput',
  alignmentConditionManualInput: 'alignmentConditionManualInput',
  alignmentMaxDurationInput: 'alignmentMaxDurationInput',
  manualAlignmentSelectedInput: 'manualAlignmentSelectedInput',
  startKeyInput: 'startKeyInput',
  endKeyInput: 'endKeyInput',
  durationKeyInput: 'durationKeyInput',
  toStepInput: 'toStepInput',
  toDiscreteInput: 'toDiscreteInput',
  produceStats: 'produceStatsInput',
  maxInterpolationInput: 'maxInterpolationInput',
} as const;

export const OUTPUT_NAMES = {
  primaryChartSignalOutput: 'primaryChartSignalOutput',
  primaryChartWE1RuleViolationOutput: 'primaryChartWE1RuleViolationOutput',
  primaryChartWE2RuleViolationOutput: 'primaryChartWE2RuleViolationOutput',
  primaryChartWE3RuleViolationOutput: 'primaryChartWE3RuleViolationOutput',
  primaryChartWE4RuleViolationOutput: 'primaryChartWE4RuleViolationOutput',
  primaryChartWE5RuleViolationOutput: 'primaryChartWE5RuleViolationOutput',
  primaryChartOverallRuleViolationOutput: 'primaryChartOverallRuleViolationOutput',
  primaryChartCenterlineOutput: 'primaryChartCenterlineOutput',
  primaryChartStandardDeviationOutput: 'primaryChartStandardDeviationOutput',
  primaryChartUclOutput: 'primaryChartUclOutput',
  primaryChartLclOutput: 'primaryChartLclOutput',
  primaryChartPlus2SigmaOutput: 'primaryChartPlus2SigmaOutput',
  primaryChartMinus2SigmaOutput: 'primaryChartMinus2SigmaOutput',
  primaryChartPlus1SigmaOutput: 'primaryChartPlus1SigmaOutput',
  primaryChartMinus1SigmaOutput: 'primaryChartMinus1SigmaOutput',
  secondaryChartSignalOutput: 'secondaryChartSignalOutput',
  secondaryChartWE1RuleViolationOutput: 'secondaryChartWE1RuleViolationOutput',
  secondaryChartWE2RuleViolationOutput: 'secondaryChartWE2RuleViolationOutput',
  secondaryChartWE3RuleViolationOutput: 'secondaryChartWE3RuleViolationOutput',
  secondaryChartWE4RuleViolationOutput: 'secondaryChartWE4RuleViolationOutput',
  secondaryChartWE5RuleViolationOutput: 'secondaryChartWE5RuleViolationOutput',
  secondaryChartOverallRuleViolationOutput: 'secondaryChartOverallRuleViolationOutput',
  secondaryChartCenterlineOutput: 'secondaryChartCenterlineOutput',
  secondaryChartStandardDeviationOutput: 'secondaryChartStandardDeviationOutput',
  secondaryChartUclOutput: 'secondaryChartUclOutput',
  secondaryChartLclOutput: 'secondaryChartLclOutput',
  secondaryChartPlus2SigmaOutput: 'secondaryChartPlus2SigmaOutput',
  secondaryChartMinus2SigmaOutput: 'secondaryChartMinus2SigmaOutput',
  secondaryChartPlus1SigmaOutput: 'secondaryChartPlus1SigmaOutput',
  secondaryChartMinus1SigmaOutput: 'secondaryChartMinus1SigmaOutput',
} as const;

export const OUTPUT_KEYS = {
  UCL: 'UclOutput',
  LCL: 'LclOutput',
  PLUS_1_SIGMA: 'Plus1SigmaOutput',
  MINUS_1_SIGMA: 'Minus1SigmaOutput',
  PLUS_2_SIGMA: 'Plus2SigmaOutput',
  MINUS_2_SIGMA: 'Minus2SigmaOutput',
} as const;

export const STATISTIC_OPTIONS = [
  { value: StatisticType.CPK, label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.STATISTICS.CPK' },
  { value: StatisticType.PPK, label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.STATISTICS.PPK' },
  { value: StatisticType.P_VALUE, label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.STATISTICS.P_VALUE' },
  {
    value: StatisticType.VARIANCE_PVALUE,
    label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.STATISTICS.VARIANCE_PVALUE',
  },
  {
    value: StatisticType.MEAN_PVALUE,
    label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.STATISTICS.MEAN_PVALUE',
  },
] as const;

export const STATISTIC_OUTPUT_FEATURE_NAMES = {
  [StatisticType.P_VALUE]: 'pValueOutput',
  [StatisticType.CPK]: 'cpkOutput',
  [StatisticType.PPK]: 'ppkOutput',
  [StatisticType.MEAN_PVALUE]: 'meanPValueOutput',
  [StatisticType.VARIANCE_PVALUE]: 'variancePValueOutput',
} as const;

export const SPC_CHARTS_MAP = {
  [I_CHART_KEY]: {
    label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.I_CHART',
  },
  [MR_CHART_KEY]: {
    label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.MR_CHART',
  },
  [XBAR_CHART_KEY]: {
    label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.XBAR_CHART',
  },
  [R_CHART_KEY]: {
    label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.R_CHART',
  },
  [S_CHART_KEY]: {
    label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.S_CHART',
  },
} as const;

export const CONTROL_CHART_OUTPUTS = {
  [PRIMARY_CHART_SLOT]: {
    SignalOutput: {
      featureName: OUTPUT_NAMES.primaryChartSignalOutput,
      label: '',
      icon: ICON_SERIES,
    },
    CenterlineOutput: {
      featureName: OUTPUT_NAMES.primaryChartCenterlineOutput,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.CENTERLINE',
      icon: ICON_SCALAR,
    },
    ControlLimitsOutput: {
      featureNames: [OUTPUT_NAMES.primaryChartUclOutput, OUTPUT_NAMES.primaryChartLclOutput],
      outputKeys: [OUTPUT_KEYS.UCL, OUTPUT_KEYS.LCL],
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.CONTROL_LIMITS',
      icon: ICON_SCALAR,
    },
    OneSigmaOutput: {
      featureNames: [OUTPUT_NAMES.primaryChartPlus1SigmaOutput, OUTPUT_NAMES.primaryChartMinus1SigmaOutput],
      outputKeys: [OUTPUT_KEYS.PLUS_1_SIGMA, OUTPUT_KEYS.MINUS_1_SIGMA],
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.ONE_SIGMA',
      icon: ICON_SCALAR,
    },
    TwoSigmaOutput: {
      featureNames: [OUTPUT_NAMES.primaryChartPlus2SigmaOutput, OUTPUT_NAMES.primaryChartMinus2SigmaOutput],
      outputKeys: [OUTPUT_KEYS.PLUS_2_SIGMA, OUTPUT_KEYS.MINUS_2_SIGMA],
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.TWO_SIGMA',
      icon: ICON_SCALAR,
    },
    WE1RuleOutput: {
      featureName: OUTPUT_NAMES.primaryChartWE1RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE1,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE1.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    WE2RuleOutput: {
      featureName: OUTPUT_NAMES.primaryChartWE2RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE2,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE2.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    WE3RuleOutput: {
      featureName: OUTPUT_NAMES.primaryChartWE3RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE3,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE3.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    WE4RuleOutput: {
      featureName: OUTPUT_NAMES.primaryChartWE4RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE4,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE4.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    OverallRuleViolationOutput: {
      featureName: OUTPUT_NAMES.primaryChartOverallRuleViolationOutput,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.UNION_OF_RUN_RULES',
      icon: ICON_CAPSULE_SET,
    },
  },
  [SECONDARY_CHART_SLOT]: {
    SignalOutput: {
      featureName: OUTPUT_NAMES.secondaryChartSignalOutput,
      label: '',
      icon: ICON_SERIES,
    },
    CenterlineOutput: {
      featureName: OUTPUT_NAMES.secondaryChartCenterlineOutput,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.CENTERLINE',
      icon: ICON_SCALAR,
    },
    ControlLimitsOutput: {
      featureNames: [OUTPUT_NAMES.secondaryChartUclOutput, OUTPUT_NAMES.secondaryChartLclOutput],
      outputKeys: [OUTPUT_KEYS.UCL, OUTPUT_KEYS.LCL],
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.CONTROL_LIMITS',
      icon: ICON_SCALAR,
    },
    WE1RuleOutput: {
      featureName: OUTPUT_NAMES.secondaryChartWE1RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE1,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE1.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    WE2RuleOutput: {
      featureName: OUTPUT_NAMES.secondaryChartWE2RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE2,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE2.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    WE3RuleOutput: {
      featureName: OUTPUT_NAMES.secondaryChartWE3RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE3,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE3.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    WE4RuleOutput: {
      featureName: OUTPUT_NAMES.secondaryChartWE4RuleViolationOutput,
      ruleName: RUN_RULES.WESTERN_ELECTRIC.WE4,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE4.SHORT',
      icon: ICON_CAPSULE_SET,
    },
    OverallRuleViolationOutput: {
      featureName: OUTPUT_NAMES.secondaryChartOverallRuleViolationOutput,
      label: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.UNION_OF_RUN_RULES',
      icon: ICON_CAPSULE_SET,
    },
  },
} as const;

export const OUTPUT_GROUP_LABELS: Record<string, string> = {
  SignalOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.SAMPLES',
  CenterlineOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.CENTERLINE',
  ControlLimitsOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.CONTROL_LIMITS',
  OneSigmaOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.ONE_SIGMA',
  TwoSigmaOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.TWO_SIGMA',
  OverallRuleViolationOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.OUTPUTS.UNION_OF_RUN_RULES',
  WE1RuleOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE1.SHORT',
  WE2RuleOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE2.SHORT',
  WE3RuleOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE3.SHORT',
  WE4RuleOutput: 'INVESTIGATE_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY.RUN_RULES.WESTERN_ELECTRIC.WE4.SHORT',
};

export const OUTPUT_GROUP_RULE_NAMES = {
  WE1RuleOutput: RUN_RULES.WESTERN_ELECTRIC.WE1,
  WE2RuleOutput: RUN_RULES.WESTERN_ELECTRIC.WE2,
  WE3RuleOutput: RUN_RULES.WESTERN_ELECTRIC.WE3,
  WE4RuleOutput: RUN_RULES.WESTERN_ELECTRIC.WE4,
} as const;

export const RUN_RULE_OUTPUT_GROUP_KEYS = new Set([
  'OverallRuleViolationOutput',
  ...Object.keys(OUTPUT_GROUP_RULE_NAMES),
]);

const SUPPORTED_KEY_METHODS = new Set<string>([TIMESTAMP_START, TIMESTAMP_END, TIMESTAMP_DURATION]);

export const TIMESTAMP_PLACEMENT_OPTIONS = _.chain(SAMPLE_FROM_SCALARS.KEY_METHODS)
  .filter((method) => SUPPORTED_KEY_METHODS.has(method.key))
  .map((method) => ({ value: method.key, label: method.title }))
  .value();
